import React, { useState, useEffect, useCallback } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalculator, faFloppyDisk, faTrash, faArrowDown, faArrowUp,
  faCalendarDays, faMoneyBillWave, faArrowRightArrowLeft, faEquals,
  faPercent, faPlus, faCircleInfo, faCoins,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import {
  UiPageHeader,
  UiFilterPanel,
  UiStatsGrid,
  UiModal,
  UiButton,
} from '../../components/ui';
import type { StatCardDef } from '../../components/ui';

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = 'Asia/Bangkok';

interface ReconciliationResult {
  orders: { count: number; total: number; fee_total: number };
  payouts: { count: number; total: number; fee_total: number };
}

interface ReconciliationRecord {
  id: string;
  date_from: string;
  date_to: string;
  orders_count: number;
  orders_total: number;
  orders_fee_total: number;
  payouts_fee_total: number;
  bank_deposit_amount: number;
  orders_difference: number;
  payouts_count: number;
  payouts_total: number;
  bank_withdrawal_amount: number;
  payouts_difference: number;
  net_system: number;
  net_bank: number;
  settlement_amount: number;
  usdt_exchange_amount: number;
  usdt_exchange_rate: number | null;
  usdt_exchange_thb: number;
  notes: string | null;
  created_by: string;
  created_at: string;
}

const fmt = (val: number | string) =>
  parseFloat(String(val) || '0').toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (iso: string) => dayjs(iso).tz(TZ).format('DD/MM/YYYY HH:mm');

const DP_INPUT_CLASS =
  'px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500 w-40';

const DiffCell: React.FC<{ value: number }> = ({ value }) => {
  if (value === 0) return <span className="text-gray-400 dark:text-slate-500 text-xs font-semibold">±฿0.00</span>;
  const pos = value > 0;
  return (
    <span className={`text-sm font-bold ${pos ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
      {pos ? '+' : '-'}฿{fmt(Math.abs(value))}
    </span>
  );
};

const ReconciliationPage: React.FC = () => {
  const [dateFrom, setDateFrom] = useState<Date>(dayjs().tz(TZ).startOf('day').toDate());
  const [dateTo,   setDateTo]   = useState<Date>(dayjs().tz(TZ).endOf('day').toDate());

  const [result,      setResult]      = useState<ReconciliationResult | null>(null);
  const [calculating, setCalculating] = useState(false);

  const [bankDeposit,      setBankDeposit]      = useState('');
  const [bankWithdrawal,   setBankWithdrawal]   = useState('');
  const [settlementAmount, setSettlementAmount] = useState('');
  const [usdtExchange,     setUsdtExchange]     = useState('');
  const [notes,            setNotes]            = useState('');
  const [saving,           setSaving]           = useState(false);

  const [usdtRate,        setUsdtRate]        = useState<number | null>(null);
  const [usdtRateLoading, setUsdtRateLoading] = useState(false);

  const [viewNotes, setViewNotes] = useState<string | null>(null);

  const [records,      setRecords]      = useState<ReconciliationRecord[]>([]);
  const [loadingList,  setLoadingList]  = useState(false);
  const [page,         setPage]         = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const ordersTotal     = result?.orders.total     ?? 0;
  const feeTotal        = result?.orders.fee_total ?? 0;
  const feeTotalPayouts = result?.payouts.fee_total ?? 0;
  const payoutsTotal    = result?.payouts.total    ?? 0;
  const deposit         = parseFloat(bankDeposit    || '0');
  const withdrawal      = parseFloat(bankWithdrawal || '0');
  const settlement      = parseFloat(settlementAmount || '0');
  const usdtExchangeNum = parseFloat(usdtExchange || '0');
  const usdtPreviewThb  = usdtRate ? parseFloat((usdtExchangeNum * usdtRate).toFixed(2)) : null;
  const ordersDiff      = parseFloat((deposit - ordersTotal).toFixed(2));
  const payoutsDiff     = parseFloat(((payoutsTotal + settlement + (usdtPreviewThb || 0)) - withdrawal).toFixed(2));
  const netSystem       = parseFloat((ordersTotal - payoutsTotal).toFixed(2));
  const netBank         = parseFloat((deposit - withdrawal - settlement).toFixed(2));
  const feePlusIo       = parseFloat((feeTotal + feeTotalPayouts + netSystem).toFixed(2));

  const loadUsdtRate = useCallback(async () => {
    setUsdtRateLoading(true);
    try {
      const res = await merchantApi.get('/admin/freeze/usdt-rate');
      setUsdtRate(res.data.data?.rate || null);
    } catch { setUsdtRate(null); }
    finally { setUsdtRateLoading(false); }
  }, []);

  const fetchRecords = useCallback(async (p = 1) => {
    setLoadingList(true);
    try {
      const res = await merchantApi.get('/admin/reconciliation', { params: { page: p, limit: 10 } });
      if (res.data.success) {
        setRecords(res.data.data || []);
        setTotalRecords(res.data.pagination?.total || 0);
      }
    } catch { toast.error('โหลดรายการไม่สำเร็จ'); }
    finally { setLoadingList(false); }
  }, []);

  useEffect(() => { fetchRecords(1); }, [fetchRecords]);

  const handleCalculate = async () => {
    setCalculating(true);
    setResult(null);
    try {
      const res = await merchantApi.get('/admin/reconciliation/calculate', {
        params: {
          date_from: dayjs(dateFrom).toISOString(),
          date_to:   dayjs(dateTo).toISOString(),
        },
      });
      if (res.data.success) { setResult(res.data.data); loadUsdtRate(); toast.success('คำนวณสำเร็จ'); }
    } catch (e: any) { toast.error(e.response?.data?.message || 'คำนวณไม่สำเร็จ'); }
    finally { setCalculating(false); }
  };

  const handleSave = async () => {
    if (!result) { toast.warning('กรุณากดคำนวณก่อนบันทึก'); return; }
    setSaving(true);
    try {
      const res = await merchantApi.post('/admin/reconciliation', {
        date_from:              dayjs(dateFrom).toISOString(),
        date_to:                dayjs(dateTo).toISOString(),
        orders_count:           result.orders.count,
        orders_total:           ordersTotal,
        orders_fee_total:       feeTotal,
        payouts_fee_total:      feeTotalPayouts,
        bank_deposit_amount:    deposit,
        payouts_count:          result.payouts.count,
        payouts_total:          payoutsTotal,
        bank_withdrawal_amount: withdrawal,
        settlement_amount:      parseFloat(settlementAmount || '0'),
        usdt_exchange_amount:   usdtExchangeNum,
        notes,
      });
      if (res.data.success) {
        toast.success('บันทึกสำเร็จ');
        setResult(null); setBankDeposit(''); setBankWithdrawal(''); setSettlementAmount(''); setUsdtExchange(''); setNotes('');
        setUsdtRate(null);
        fetchRecords(1); setPage(1);
      }
    } catch (e: any) { toast.error(e.response?.data?.message || 'บันทึกไม่สำเร็จ'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('ยืนยันการลบรายการนี้?')) return;
    try {
      await merchantApi.delete(`/admin/reconciliation/${id}`);
      toast.success('ลบสำเร็จ');
      fetchRecords(page);
    } catch { toast.error('ลบไม่สำเร็จ'); }
  };

  const handlePageChange = (p: number) => { setPage(p); fetchRecords(p); };

  const statsCards: StatCardDef[] = [
    {
      icon: faArrowDown,
      iconClassName: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-500 dark:text-emerald-400',
      label: 'Payin (หลัง fee)',
      value: result
        ? <span className="text-emerald-600 dark:text-emerald-400">฿{fmt(ordersTotal)}</span>
        : <span className="text-gray-200 dark:text-slate-600">—</span>,
      subValue: result
        ? <span className="text-xs text-gray-400 dark:text-slate-500">{result.orders.count} รายการ • Gross − Fee</span>
        : <span className="text-xs text-gray-300 dark:text-slate-600">กดคำนวณเพื่อดูข้อมูล</span>,
    },
    {
      icon: faArrowUp,
      iconClassName: 'bg-rose-50 dark:bg-rose-900/30 text-rose-500 dark:text-rose-400',
      label: 'Payout',
      value: result
        ? <span className="text-rose-600 dark:text-rose-400">฿{fmt(payoutsTotal)}</span>
        : <span className="text-gray-200 dark:text-slate-600">—</span>,
      subValue: result
        ? <span className="text-xs text-gray-400 dark:text-slate-500">{result.payouts.count} รายการ</span>
        : undefined,
    },
    {
      icon: faPercent,
      iconClassName: 'bg-amber-50 dark:bg-amber-900/30 text-amber-500 dark:text-amber-400',
      label: 'ค่าธรรมเนียม',
      value: result
        ? <span className="text-amber-600 dark:text-amber-400">฿{fmt(feeTotal + feeTotalPayouts)}</span>
        : <span className="text-gray-200 dark:text-slate-600">—</span>,
      subValue: result ? (
        <span className="text-xs">
          <span className="text-amber-600 dark:text-amber-400">In ฿{fmt(feeTotal)}</span>
          <span className="text-gray-300 dark:text-slate-600 mx-1">|</span>
          <span className="text-indigo-600 dark:text-indigo-400">Out ฿{fmt(feeTotalPayouts)}</span>
        </span>
      ) : undefined,
    },
    {
      icon: faMoneyBillWave,
      iconClassName: 'bg-slate-50 dark:bg-slate-700',
      iconStyle: { color: 'var(--theme-accent)' },
      label: 'ส่วนต่าง IO',
      value: result
        ? <span style={{ color: 'var(--theme-accent)' }}>฿{fmt(netSystem)}</span>
        : <span className="text-gray-200 dark:text-slate-600">—</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">Payin − Payout</span>,
    },
    {
      icon: faPlus,
      iconClassName: 'bg-violet-50 dark:bg-violet-900/30 text-violet-500 dark:text-violet-400',
      label: 'ผลรวม Fee+IO',
      value: result
        ? <span className="text-violet-600 dark:text-violet-400">฿{fmt(feePlusIo)}</span>
        : <span className="text-gray-200 dark:text-slate-600">—</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">Fee (In+Out) + ส่วนต่าง IO</span>,
    },
    {
      icon: faArrowRightArrowLeft,
      iconClassName: 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400',
      label: 'ธนาคารสุทธิ',
      value: (deposit > 0 || withdrawal > 0)
        ? <span className="text-gray-800 dark:text-slate-100">฿{fmt(netBank)}</span>
        : <span className="text-gray-200 dark:text-slate-600">—</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">รับ − จ่าย − Settlement</span>,
    },
  ];

  const columns: Column<ReconciliationRecord>[] = [
    {
      key: 'date_from',
      header: 'ช่วงวันที่',
      render: (r) => (
        <div className="text-xs text-gray-600 dark:text-slate-300">
          <div className="font-medium">{fmtDate(r.date_from)}</div>
          <div className="text-gray-400 dark:text-slate-500">→ {fmtDate(r.date_to)}</div>
        </div>
      ),
    },
    {
      key: 'orders_total',
      header: 'ยอด Orders',
      headerClassName: 'text-right',
      className: 'text-right',
      render: (r) => (
        <>
          <span className="font-semibold text-emerald-600 dark:text-emerald-400">฿{fmt(r.orders_total)}</span>
          <div className="text-xs text-gray-400 dark:text-slate-500">{r.orders_count} รายการ</div>
        </>
      ),
    },
    {
      key: 'bank_deposit_amount',
      header: 'ธนาคารรับ',
      headerClassName: 'text-right',
      className: 'text-right',
      render: (r) => <span className="text-gray-600 dark:text-slate-300">฿{fmt(r.bank_deposit_amount)}</span>,
    },
    {
      key: 'orders_diff',
      header: 'ผลต่างรับ',
      headerClassName: 'text-right',
      className: 'text-right',
      render: (r) => <DiffCell value={parseFloat((r.bank_deposit_amount - r.orders_total).toFixed(2))} />,
    },
    {
      key: 'payouts_total',
      header: 'ยอด Payouts',
      headerClassName: 'text-right',
      className: 'text-right',
      render: (r) => (
        <>
          <span className="font-semibold text-rose-600 dark:text-rose-400">฿{fmt(r.payouts_total)}</span>
          <div className="text-xs text-gray-400 dark:text-slate-500">{r.payouts_count} รายการ</div>
        </>
      ),
    },
    {
      key: 'bank_withdrawal_amount',
      header: 'ธนาคารจ่าย',
      headerClassName: 'text-right',
      className: 'text-right',
      render: (r) => <span className="text-gray-600 dark:text-slate-300">฿{fmt(r.bank_withdrawal_amount)}</span>,
    },
    {
      key: 'payouts_diff',
      header: 'ผลต่างจ่าย',
      headerClassName: 'text-right',
      className: 'text-right',
      // ผลต่างจ่าย = (Payouts + Settlement + แลก USDT เป็นบาท) − ธนาคารจ่าย
      render: (r) => <DiffCell value={parseFloat(((r.payouts_total + (r.settlement_amount || 0) + (r.usdt_exchange_thb || 0)) - r.bank_withdrawal_amount).toFixed(2))} />,
    },
    {
      key: 'settlement_amount',
      header: 'Settlement',
      headerClassName: 'text-right',
      className: 'text-right',
      render: (r) => r.settlement_amount > 0
        ? <span className="text-violet-600 dark:text-violet-400 font-medium">฿{fmt(r.settlement_amount)}</span>
        : <span className="text-gray-300 dark:text-slate-600">—</span>,
    },
    {
      key: 'usdt_exchange',
      header: 'แลก USDT',
      headerClassName: 'text-right',
      className: 'text-right',
      render: (r) => (r.usdt_exchange_amount || 0) > 0 ? (
        <>
          <div className="text-teal-600 dark:text-teal-400 font-semibold">{fmt(r.usdt_exchange_amount)} USDT</div>
          <div className="text-[10px] text-gray-400 dark:text-slate-500">
            ฿{fmt(r.usdt_exchange_thb || 0)}
            {r.usdt_exchange_rate ? ` @ ${fmt(r.usdt_exchange_rate)}` : ''}
          </div>
        </>
      ) : <span className="text-gray-300 dark:text-slate-600">—</span>,
    },
    {
      key: 'fee',
      header: 'Fee (In/Out)',
      headerClassName: 'text-right',
      className: 'text-right',
      render: (r) => (
        <>
          <div className="text-[10px] text-amber-600 dark:text-amber-400 font-bold">In: ฿{fmt(r.orders_fee_total)}</div>
          <div className="text-[10px] text-indigo-600 dark:text-indigo-400 font-bold">Out: ฿{fmt(r.payouts_fee_total || 0)}</div>
        </>
      ),
    },
    {
      key: 'net_system',
      header: 'ส่วนต่าง io',
      headerClassName: 'text-right',
      className: 'text-right',
      render: (r) => (
        <span className="font-bold" style={{ color: 'var(--theme-accent)' }}>฿{fmt(r.net_system)}</span>
      ),
    },
    {
      key: 'fee_plus_io',
      header: 'Fee+io รวม',
      headerClassName: 'text-right',
      className: 'text-right',
      render: (r) => {
        const total = (r.orders_fee_total || 0) + (r.payouts_fee_total || 0) + (r.net_system || 0);
        return <span className="font-bold text-violet-600 dark:text-violet-400">฿{fmt(total.toFixed(2))}</span>;
      },
    },
    {
      key: 'net_bank',
      header: 'ธนาคารสุทธิ',
      headerClassName: 'text-right',
      className: 'text-right',
      render: (r) => <span className="font-bold text-gray-700 dark:text-slate-200">฿{fmt(r.net_bank)}</span>,
    },
    {
      key: 'created_by',
      header: 'บันทึกโดย',
      render: (r) => (
        <>
          <div className="text-xs text-gray-600 dark:text-slate-300 font-medium">{r.created_by}</div>
          <div className="text-[10px] text-gray-400 dark:text-slate-500">{fmtDate(r.created_at)}</div>
        </>
      ),
    },
    {
      key: 'notes',
      header: 'หมายเหตุ',
      render: (r) => r.notes ? (
        <button
          onClick={() => setViewNotes(r.notes)}
          className="text-left group"
          title="คลิกเพื่อดูทั้งหมด"
        >
          <span className="text-xs text-gray-500 dark:text-slate-400 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors line-clamp-2 break-words">
            {r.notes.length > 50 ? r.notes.slice(0, 50) + '…' : r.notes}
          </span>
        </button>
      ) : <span className="text-gray-300 dark:text-slate-600 text-xs">—</span>,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (r) => (
        <button
          onClick={() => handleDelete(r.id)}
          className="text-gray-300 dark:text-slate-600 hover:text-red-500 dark:hover:text-red-400 p-1 transition-colors"
        >
          <FontAwesomeIcon icon={faTrash} className="text-xs" />
        </button>
      ),
    },
  ];

  return (
    <div className="max-w-full overflow-x-hidden">

      <UiPageHeader
        icon={faCalculator}
        title="ตัดยอดย้อนหลัง"
        subtitle="เลือกช่วงวันที่ • กรอกยอดธนาคาร • บันทึก"
      />

      {/* Filter / Calculate */}
      <UiFilterPanel onSearch={handleCalculate} loading={calculating}>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
              <FontAwesomeIcon icon={faCalendarDays} className="mr-1" />
              วันที่เริ่มต้น
            </label>
            <DatePicker
              selected={dateFrom}
              onChange={(d: Date | null) => d && setDateFrom(d)}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="dd/MM/yyyy HH:mm"
              className={DP_INPUT_CLASS}
              popperProps={{ strategy: 'fixed' }}
              popperClassName="!z-[9999]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
              <FontAwesomeIcon icon={faCalendarDays} className="mr-1" />
              วันที่สิ้นสุด
            </label>
            <DatePicker
              selected={dateTo}
              onChange={(d: Date | null) => d && setDateTo(d)}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="dd/MM/yyyy HH:mm"
              minDate={dateFrom}
              className={DP_INPUT_CLASS}
              popperProps={{ strategy: 'fixed' }}
              popperClassName="!z-[9999]"
            />
          </div>
        </div>
      </UiFilterPanel>

      {/* Summary Stats */}
      <UiStatsGrid cards={statsCards} loading={calculating} cols={3} />

      {/* Input Panel — shown after calculate */}
      {result && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-200 dark:border-slate-700 p-6 mb-6">
          <h2 className="font-semibold text-gray-700 dark:text-slate-200 text-sm uppercase tracking-wide mb-4 flex items-center gap-2">
            <FontAwesomeIcon icon={faEquals} className="text-gray-400 dark:text-slate-500" />
            กรอกยอดธนาคารและตรวจสอบส่วนต่าง
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Orders side */}
            <div className="space-y-3">
              <div className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide flex items-center gap-1.5 mb-2">
                <FontAwesomeIcon icon={faArrowDown} />
                ฝั่งรับเงิน (Orders paid)
              </div>
              <div className="flex items-center justify-between text-sm py-2 px-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
                <span className="text-gray-500 dark:text-slate-400">
                  ยอด Payin (หลัง fee) <span className="text-xs text-gray-400 dark:text-slate-500">({result.orders.count} รายการ)</span>
                </span>
                <span className="font-bold text-emerald-600 dark:text-emerald-400">฿{fmt(ordersTotal)}</span>
              </div>
              <div className="flex items-center justify-between text-sm py-2 px-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg">
                <span className="text-gray-500 dark:text-slate-400">ยอดรับจากธนาคาร</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-gray-400 dark:text-slate-500 text-xs">฿</span>
                  <input
                    type="number" min="0" step="0.01"
                    value={bankDeposit}
                    onChange={(e) => setBankDeposit(e.target.value)}
                    placeholder="0.00"
                    className="w-32 text-right border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 placeholder:text-gray-300 dark:placeholder:text-slate-600"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between text-sm py-2 px-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
                <span className="text-gray-500 dark:text-slate-400">ส่วนต่าง (ธนาคาร − Payin)</span>
                <DiffCell value={ordersDiff} />
              </div>
              <div className="flex items-center justify-between text-sm py-2 px-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <span className="text-amber-700 dark:text-amber-400 font-medium">ค่าธรรมเนียม (Fee)</span>
                <span className="font-bold text-amber-600 dark:text-amber-400">฿{fmt(feeTotal)}</span>
              </div>
            </div>

            {/* Payouts side */}
            <div className="space-y-3">
              <div className="text-xs font-semibold text-rose-600 dark:text-rose-400 uppercase tracking-wide flex items-center gap-1.5 mb-2">
                <FontAwesomeIcon icon={faArrowUp} />
                ฝั่งจ่ายเงิน (Payouts completed)
              </div>
              <div className="flex items-center justify-between text-sm py-2 px-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
                <span className="text-gray-500 dark:text-slate-400">
                  ยอด Payouts <span className="text-xs text-gray-400 dark:text-slate-500">({result.payouts.count} รายการ)</span>
                </span>
                <span className="font-bold text-rose-600 dark:text-rose-400">฿{fmt(payoutsTotal)}</span>
              </div>
              <div className="flex items-center justify-between text-sm py-2 px-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg">
                <span className="text-gray-500 dark:text-slate-400">ยอดจ่ายออกธนาคาร</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-gray-400 dark:text-slate-500 text-xs">฿</span>
                  <input
                    type="number" min="0" step="0.01"
                    value={bankWithdrawal}
                    onChange={(e) => setBankWithdrawal(e.target.value)}
                    placeholder="0.00"
                    className="w-32 text-right border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 placeholder:text-gray-300 dark:placeholder:text-slate-600"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between text-sm py-2 px-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
                <span className="text-gray-500 dark:text-slate-400">ส่วนต่าง (Payout + Settlement + USDT − ธนาคาร)</span>
                <DiffCell value={payoutsDiff} />
              </div>
              <div className="flex items-center justify-between text-sm py-2 px-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                <span className="text-indigo-700 dark:text-indigo-400 font-medium">ค่าธรรมเนียมถอนเงิน (Payout Fee)</span>
                <span className="font-bold text-indigo-600 dark:text-indigo-400">฿{fmt(feeTotalPayouts)}</span>
              </div>
            </div>
          </div>

          {/* Settlement */}
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-slate-700">
            <div className="text-xs font-semibold text-violet-600 dark:text-violet-400 uppercase tracking-wide flex items-center gap-1.5 mb-2">
              <FontAwesomeIcon icon={faArrowRightArrowLeft} />
              Settlement
            </div>
            <div className="flex items-center justify-between text-sm py-2 px-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg max-w-sm">
              <span className="text-gray-500 dark:text-slate-400">ยอด Settlement</span>
              <div className="flex items-center gap-1.5">
                <span className="text-gray-400 dark:text-slate-500 text-xs">฿</span>
                <input
                  type="number" min="0" step="0.01"
                  value={settlementAmount}
                  onChange={(e) => setSettlementAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-32 text-right border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 dark:focus:ring-violet-600 placeholder:text-gray-300 dark:placeholder:text-slate-600"
                />
              </div>
            </div>
          </div>

          {/* USDT Exchange */}
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-slate-700">
            <div className="text-xs font-semibold text-teal-600 dark:text-teal-400 uppercase tracking-wide flex items-center gap-1.5 mb-2">
              <FontAwesomeIcon icon={faCoins} />
              ยอดแลก USDT
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 max-w-sm">
              <div className="flex items-center justify-between text-sm py-2 px-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg flex-1">
                <span className="text-gray-500 dark:text-slate-400">ยอดแลก (USDT)</span>
                <div className="flex items-center gap-1.5">
                  <input
                    type="number" min="0" step="0.01"
                    value={usdtExchange}
                    onChange={(e) => setUsdtExchange(e.target.value)}
                    placeholder="0.00"
                    className="w-28 text-right border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 dark:focus:ring-teal-600 placeholder:text-gray-300 dark:placeholder:text-slate-600"
                  />
                  <span className="text-gray-400 dark:text-slate-500 text-xs">USDT</span>
                </div>
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500 dark:text-slate-400 max-w-sm flex items-center justify-between px-1">
              <span>
                เรท (Bitkub):{' '}
                {usdtRateLoading
                  ? <span className="text-gray-400 dark:text-slate-500">กำลังโหลด…</span>
                  : usdtRate
                    ? <span className="font-medium text-teal-600 dark:text-teal-400">฿{fmt(usdtRate)} / USDT</span>
                    : <span className="text-red-400">โหลด rate ไม่ได้</span>}
              </span>
              {usdtExchangeNum > 0 && usdtPreviewThb !== null && (
                <span className="font-semibold text-teal-600 dark:text-teal-400">≈ ฿{fmt(usdtPreviewThb)}</span>
              )}
            </div>
            <p className="mt-1 text-[11px] text-gray-400 dark:text-slate-500 max-w-sm px-1">
              * ระบบจะดึงเรทจริงจาก Bitkub ตอนกดบันทึกอีกครั้งเพื่อความแม่นยำ
            </p>
          </div>

          {/* Notes + Save */}
          <div className="flex flex-col sm:flex-row gap-3 mt-5 pt-5 border-t border-gray-100 dark:border-slate-700">
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="หมายเหตุ (ไม่บังคับ)"
              className="flex-1 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 placeholder:text-gray-400 dark:placeholder:text-slate-500"
            />
            <UiButton onClick={handleSave} loading={saving} icon={faFloppyDisk}>
              บันทึกการตัดยอด
            </UiButton>
          </div>
        </div>
      )}

      {/* History Table */}
      <DataTable
        data={records}
        columns={columns}
        loading={loadingList}
        keyExtractor={(r) => r.id}
        compact
        serverPagination={{
          currentPage: page,
          totalItems: totalRecords,
          pageSize: 10,
          onPageChange: handlePageChange,
        }}
        headerContent={
          <h2 className="font-semibold text-gray-700 dark:text-slate-200 text-sm uppercase tracking-wide">
            ประวัติการตัดยอด
            {totalRecords > 0 && (
              <span className="ml-2 text-gray-400 dark:text-slate-500 font-normal normal-case">({totalRecords} รายการ)</span>
            )}
          </h2>
        }
      />

      {/* Notes Modal */}
      <UiModal
        open={!!viewNotes}
        onClose={() => setViewNotes(null)}
        title="หมายเหตุ"
        icon={faCircleInfo}
        iconColor="text-blue-500"
        size="sm"
      >
        <p className="text-sm text-gray-600 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
          {viewNotes}
        </p>
      </UiModal>

    </div>
  );
};

export default ReconciliationPage;
